/**
 * iter-170o — Inline cap-usage badge
 *
 * Tiny component that renders "X/Y used" next to a feature title.
 * Switches to red at hard limit, amber at soft limit (80%+),
 * neutral otherwise. Hides when unlimited or when allowance is 0
 * AND used is 0 (feature not in any plan).
 *
 * iter-189 — When the badge is at soft or hard limit it becomes a
 * clickable Link to /billing/my-plan so the user can act on the
 * warning without hunting for the upgrade page. Below limits it
 * stays a passive label (no link, no hover affordance) — this keeps
 * the badge silent until it has something useful to say.
 */
import { useResourceCap } from "../hooks/useResourceCap";
import { AlertTriangle, Infinity } from "lucide-react";
import { Link } from "react-router-dom";

// iter-195 — resource → human noun map. Used inline so "4/4" becomes
// "4/4 cuentas" giving the user immediate context for what's
// constrained.
const RESOURCE_LABELS = {
  cash_accounts: { es: "cuentas", en: "accounts" },
  goals: { es: "metas", en: "goals" },
  investments: { es: "inversiones", en: "investments" },
  debts: { es: "deudas", en: "debts" },
  movements_monthly: { es: "mov/mes", en: "mov/mo" },
  receipts_monthly: { es: "recibos/mes", en: "receipts/mo" },
  ai_chat_monthly: { es: "msgs IA/mes", en: "AI msgs/mo" },
  pdf_export_monthly: { es: "PDFs/mes", en: "PDFs/mo" },
  workspaces: { es: "workspaces", en: "workspaces" },
  shared_ops_monthly: { es: "compartidas/mes", en: "shared/mo" },
};

export function CapUsageBadge({ resourceKey, isEs, showLabel = false }) {
  const cap = useResourceCap(resourceKey);
  if (cap.loading || cap.error) return null;
  const label = RESOURCE_LABELS[resourceKey]?.[isEs ? "es" : "en"] || "";
  if (cap.unlimited) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100"
        data-testid={`cap-badge-${resourceKey}`}
        title={isEs ? `Sin límite (${label || "recurso"})` : `Unlimited (${label || "resource"})`}
      >
        <Infinity className="h-3 w-3" />
        {showLabel && label && <span>{label}</span>}
      </span>
    );
  }
  // iter-195 — when limit is 0 but used > 0, the resource is effectively
  // unlimited / not metered by the plan (admin grant, beta access, etc.).
  // Render it as an inert info pill instead of the misleading "4/0".
  if (cap.limit === 0 && cap.used > 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100"
        data-testid={`cap-badge-${resourceKey}`}
        title={isEs ? `${cap.used} ${label || "recurso(s)"} (sin límite del plan)` : `${cap.used} ${label || "resource(s)"} (no plan limit)`}
      >
        <span className="tabular-nums">{cap.used}</span>
        {showLabel && label && <span>{label}</span>}
      </span>
    );
  }
  if (cap.limit === 0 && cap.used === 0) return null;
  const tone = cap.atHardLimit
    ? "bg-red-50 text-red-700 border-red-200"
    : cap.atSoftLimit
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  const interactive = cap.atSoftLimit || cap.atHardLimit;
  const content = (
    <>
      {cap.atHardLimit && <AlertTriangle className="h-2.5 w-2.5" />}
      <span className="tabular-nums">{cap.used}/{cap.limit}</span>
      {showLabel && label && <span>{label}</span>}
    </>
  );
  const baseCls = `inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tone}`;
  // iter-195 — Tooltip now ALWAYS spells out the resource name and the
  // limit so the user reads "4 de 4 cuentas usadas" instead of a bare
  // "4/4" pill. When at soft/hard limit it also nudges to the upgrade
  // page (Link wrapper handles the click).
  const tooltip = isEs
    ? `${cap.used} de ${cap.limit} ${label || "recurso(s)"} usado(s)${interactive ? " · clic para comprar packs / actualizar plan" : ""}`
    : `${cap.used} of ${cap.limit} ${label || "resource(s)"} used${interactive ? " · click to buy packs / upgrade" : ""}`;
  if (interactive) {
    return (
      <Link
        to="/billing/my-plan"
        className={`${baseCls} hover:opacity-80 transition-opacity`}
        data-testid={`cap-badge-${resourceKey}`}
        title={tooltip}
      >
        {content}
      </Link>
    );
  }
  return (
    <span
      className={baseCls}
      data-testid={`cap-badge-${resourceKey}`}
      title={tooltip}
    >
      {content}
    </span>
  );
}
